import { useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import type { AuthSession, FinanceCapabilities, ManagerOption } from "../api/types";

const SCREEN_PREVIEW_STORAGE_KEY = "project-selmo.admin-screen-preview-manager";

const STAFF_FINANCE_CAPABILITIES: FinanceCapabilities = {
  canRequestMoney: true,
  canConfirmMoney: false,
  canManageBankLedger: false,
  canViewPayouts: false,
  canExecuteBank: false,
};

const FINANCE_ADMIN_CAPABILITIES: FinanceCapabilities = {
  canRequestMoney: false,
  canConfirmMoney: true,
  canManageBankLedger: true,
  canViewPayouts: true,
  // A screen preview must not infer the selected person's separate bank-executor allowlist.
  canExecuteBank: false,
};

export interface AdminScreenPreviewOption {
  value: string;
  label: string;
}

export function loginScreenManagers(managers: ManagerOption[], sessionEmail?: string): ManagerOption[] {
  const currentEmail = sessionEmail?.trim().toLowerCase();
  return managers.filter((manager) => {
    const active = manager.active !== false && manager.status !== "inactive";
    const email = manager.email?.trim().toLowerCase();
    return active && Boolean(email) && email !== currentEmail;
  });
}

export function loginScreenRole(manager: ManagerOption): Extract<AuthSession["role"], "STAFF" | "FINANCE_ADMIN"> {
  return manager.roleCategory === "FINANCE" ? "FINANCE_ADMIN" : "STAFF";
}

export function loginScreenFinanceCapabilities(manager: ManagerOption): FinanceCapabilities {
  return loginScreenRole(manager) === "FINANCE_ADMIN"
    ? FINANCE_ADMIN_CAPABILITIES
    : STAFF_FINANCE_CAPABILITIES;
}

function storedManagerId(session: AuthSession): string {
  if (session.role !== "ADMIN" || typeof window === "undefined") return "";
  return window.sessionStorage?.getItem(SCREEN_PREVIEW_STORAGE_KEY)?.trim() ?? "";
}

export function useAdminScreenPreview(session: AuthSession) {
  const enabled = session.role === "ADMIN";
  const [managers, setManagers] = useState<ManagerOption[]>([]);
  const [selectedManagerId, setSelectedManagerId] = useState(() => storedManagerId(session));
  const [loading, setLoading] = useState(enabled);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setManagers([]);
      setSelectedManagerId("");
      setLoading(false);
      setLoadFailed(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setLoadFailed(false);
    api.adminManagers(controller.signal)
      .then((items) => setManagers(loginScreenManagers(items, session.email)))
      .catch((error) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          setManagers([]);
          setLoadFailed(true);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [enabled, session.email]);

  const selectedManager = managers.find((manager) => manager.id === selectedManagerId);

  useEffect(() => {
    if (!enabled || loading || !selectedManagerId || selectedManager) return;
    setSelectedManagerId("");
    window.sessionStorage?.removeItem(SCREEN_PREVIEW_STORAGE_KEY);
  }, [enabled, loading, selectedManager, selectedManagerId]);

  const selectManager = (managerId: string) => {
    const nextId = managerId.trim();
    setSelectedManagerId(nextId);
    if (!enabled || typeof window === "undefined") return;
    if (nextId) window.sessionStorage?.setItem(SCREEN_PREVIEW_STORAGE_KEY, nextId);
    else window.sessionStorage?.removeItem(SCREEN_PREVIEW_STORAGE_KEY);
  };

  const effectiveSession = useMemo<AuthSession>(() => {
    if (!selectedManager) return session;
    const role = loginScreenRole(selectedManager);
    return {
      ...session,
      email: selectedManager.email?.trim() || undefined,
      name: selectedManager.name,
      picture: undefined,
      role,
      managerId: selectedManager.id,
      capabilities: role === "STAFF" ? session.capabilities : [],
      integratedSubjectFingerprint: undefined,
    };
  }, [selectedManager, session]);

  const options = useMemo<AdminScreenPreviewOption[]>(() => [
    { value: "", label: `${session.name?.trim() || "셀모"} · 관리자 화면` },
    ...managers.map((manager) => ({
      value: manager.id,
      label: `${manager.name} · ${loginScreenRole(manager) === "FINANCE_ADMIN" ? "재무담당" : "일반 직원"}`,
    })),
  ], [managers, session.name]);

  return {
    active: Boolean(selectedManager),
    effectiveSession,
    financeCapabilities: selectedManager ? loginScreenFinanceCapabilities(selectedManager) : undefined,
    loadFailed,
    loading,
    options,
    reset: () => selectManager(""),
    selectedManager,
    selectedManagerId: selectedManager?.id ?? "",
    selectManager,
    resolving: Boolean(selectedManagerId) && loading,
  };
}
